import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useSettings, useToast } from "../App";
import { api } from "../lib/api";
import type { Project } from "../lib/types";

export default function Dashboard() {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const { pushError } = useToast();
  const { health } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    api.listProjects().then(setProjects).catch(pushError);
  }, [pushError]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const project = await api.createProject({
        name: name.trim(),
        subject: subject.trim() || undefined,
        deck_name: name.trim(),
      });
      navigate(`/projects/${project.id}`);
    } catch (error) {
      pushError(error);
      setBusy(false);
    }
  };

  const remove = async (project: Project) => {
    if (!window.confirm(`Delete "${project.name}" and all its cards? This cannot be undone.`)) return;
    try {
      await api.deleteProject(project.id);
      setProjects((current) => (current ?? []).filter((p) => p.id !== project.id));
    } catch (error) {
      pushError(error);
    }
  };

  const blocking = health?.checks.filter((c) => !c.ok) ?? [];

  return (
    <main className="page">
      <div className="page-head">
        <div className="grow">
          <h1>Your decks</h1>
          <p>Turn study materials into Anki cards you'd actually want to review.</p>
        </div>
        {!creating && (
          <button className="btn-primary btn-lg" onClick={() => setCreating(true)}>
            + New deck
          </button>
        )}
      </div>

      {blocking.length > 0 && (
        <div className="stack" style={{ marginBottom: 18 }}>
          {blocking.map((check) => (
            <div key={check.name} className="alert alert-warn">
              <span>⚠</span>
              <div>
                <strong>{check.name}: {check.detail}</strong>
                <div className="alert-hint">{check.impact}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <form className="panel" onSubmit={create} style={{ marginBottom: 18 }}>
          <h2 style={{ marginBottom: 12 }}>Create your deck</h2>
          <div className="grid grid-2">
            <label className="field">
              <span className="label">Deck name</span>
              <input
                type="text"
                value={name}
                autoFocus
                placeholder="e.g. Cell Biology"
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="label">Subject (optional)</span>
              <input
                type="text"
                value={subject}
                placeholder="e.g. Biology"
                onChange={(e) => setSubject(e.target.value)}

              />
              <span className="hint">Added as a tag on every card.</span>
            </label>
          </div>
          <div className="row">
            <button className="btn-primary" type="submit" disabled={busy || !name.trim()}>
              {busy ? "Creating…" : "Create deck"}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setCreating(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {projects === null && <p className="muted">Loading…</p>}

      {projects !== null && projects.length === 0 && !creating && (
        <div className="empty">
          <h3>No decks yet</h3>
          <p>
            Create one, upload your notes, and let the AI propose cards. You review them before
            anything is exported.
          </p>
          <button className="btn-primary" onClick={() => setCreating(true)}>
            + New deck
          </button>
        </div>
      )}

      {projects && projects.length > 0 && (
        <div className="grid grid-3">
          {projects.map((project) => (
            <div key={project.id} className="tile">
              <Link to={`/projects/${project.id}`} style={{ color: "inherit" }}>
                <h3>{project.name}</h3>
              </Link>
              <div className="row row-wrap small muted" style={{ marginTop: 6 }}>
                {project.subject && <span className="chip">{project.subject}</span>}
                {project.course && <span className="chip">{project.course}</span>}
              </div>
              <div className="row small faint" style={{ marginTop: 10 }}>
                <span>Updated {new Date(project.updated_at).toLocaleDateString()}</span>
                <div className="grow" />
                <button
                  className="btn-ghost btn-sm"
                  onClick={() => remove(project)}
                  title="Delete deck"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
